import { useEffect, useState } from 'react';
import { Alert, Box, Checkbox, Chip, Tooltip } from '@mui/material';
import * as Icon from '@mui/icons-material/';
import { toast } from 'react-toastify';

import ComponentTable from '../../../Components/Table/Index';
import { Column } from '../../../Components/Table/Types';
import FormBuilder from '../../../../shared/components/FormBuilder';
import { Button } from '../../../../shared/components/button/Button';
import { formatCurrency } from '../../../../shared/utils/helpers/numbers';
import DevolucaoFornecedorController from '../../../../controllers/DevolucaoFornecedorController';
import ModalComporNota from '../Modal/ModalComporNota';

/**
 * Tela 3 — as devoluções registradas, e a seleção para compor uma nota fiscal.
 *
 * Duas coisas moram aqui, e as duas faltavam:
 *
 *   1. CANCELAR um registro. A procedure e a rota existem desde a fase 1 e nenhuma tela
 *      as chamava — dava para criar e não dava para desfazer;
 *   2. SELECIONAR registros para virar nota, que é o começo da fase 3.
 *
 * A trava de um fornecedor só é verificada AQUI porque é aqui que a seleção existe. Uma
 * nota fiscal tem um destinatário; misturar fornecedores produziria um documento
 * impossível, e descobrir isso só na composição seria tarde.
 */
interface Registro {
  dvf_id: number;
  data_criacao: string;
  status: string;
  cli_id: number;
  fornecedor?: string;
  prod_id: number;
  codigo_promatec?: string;
  produto?: string;
  quantidade: number;
  motivo?: string;
  nfeIt_id: number;
  nota_origem?: number;
  nfe_entrada_id?: number;
  nota_devolucao?: number;
  valor_unitario: number;
  valor_total: number;
}

export const AbaRegistros = () => {
  const [registros, setRegistros] = useState<Registro[]>([]);
  const [linhas, setLinhas] = useState<any[]>([]);
  const [selecionados, setSelecionados] = useState<number[]>([]);
  const [consultou, setConsultou] = useState(false);
  const [ultimaBusca, setUltimaBusca] = useState<any>(null);
  const [comporAberto, setComporAberto] = useState(false);

  const tableColumns: Column[] = [
    { id: 'Sel', label: '', minWidth: 0, maxWidth: 50, align: 'center' },
    { id: 'Codigo', label: 'Devolução', minWidth: 0, maxWidth: 100 },
    { id: 'Data', label: 'Criada em', minWidth: 0, maxWidth: 110 },
    { id: 'Fornecedor', label: 'Fornecedor', minWidth: 140, maxWidth: 280 },
    { id: 'Produto', label: 'Produto', minWidth: 140, maxWidth: 280 },
    /* A nota de origem vem ANTES do valor, e não depois. Alinhada à esquerda logo
       após uma coluna alinhada à direita, ela encostava no dinheiro: "R$ 91,43" e
       "633009" saíam colados, e o olho lia "R$ 91,43633009". Aqui ela fica junto do
       produto, que é o contexto dela, e as duas colunas numéricas ficam lado a lado
       com a folga das próprias células. */
    { id: 'NotaOrigem', label: 'Nota origem', minWidth: 0, maxWidth: 110 },
    { id: 'Qtd', label: 'Qtd.', minWidth: 0, maxWidth: 80, align: 'right' },
    { id: 'Valor', label: 'Valor', minWidth: 0, maxWidth: 110, align: 'right' },
    { id: 'Status', label: 'Status', minWidth: 0, maxWidth: 130 },
    { id: 'Acao', label: '', minWidth: 0, maxWidth: 120, align: 'right' },
  ];

  const fields = [
    { name: 'DvfId', type: 'text', label: 'Código', col: 2 },
    { name: 'FornecedorNome', type: 'text', label: 'Fornecedor', col: 3 },
    { name: 'NotaOrigem', type: 'text', label: 'Nota de origem', col: 2 },
    { name: 'DataInicio', type: 'date', label: 'Criada de', col: 2 },
    { name: 'DataFim', type: 'date', label: 'até', col: 2 },
    { type: 'submit', icon: <Icon.Search style={{ fontSize: '28px' }} /> },
  ];

  const initialValues = {
    DvfId: '',
    FornecedorNome: '',
    NotaOrigem: '',
    DataInicio: '',
    DataFim: '',
  };

  /* O fornecedor da seleção atual. É ele que decide quais caixas continuam clicáveis:
     uma nota fiscal tem UM destinatário. */
  const fornecedorDaSelecao = (() => {
    if (selecionados.length === 0) return null;
    const primeiro = registros.find((r) => r.dvf_id === selecionados[0]);
    return primeiro?.cli_id ?? null;
  })();

  const alternar = (dvf_id: number) => {
    setSelecionados((atual) =>
      atual.includes(dvf_id) ? atual.filter((x) => x !== dvf_id) : [...atual, dvf_id]
    );
  };

  const consultar = async (values: any) => {
    const request = {
      DvfId: values.DvfId ? Number(values.DvfId) : null,
      FornecedorNome: values.FornecedorNome || null,
      NfeEntradaId: null,
      DataInicio: values.DataInicio || null,
      DataFim: values.DataFim || null,
      // Só o que pode compor nota. Registro já em nota apareceria para ser escolhido de
      // novo, e entraria em duas notas.
      SomenteDisponiveis: true,
    };

    const response = await DevolucaoFornecedorController.Listar(request);
    setConsultou(true);
    setUltimaBusca(request);
    setSelecionados([]);

    if (response.StatusCode === 200 && Array.isArray(response.Data)) {
      setRegistros(response.Data);
      if (response.Data.length === 0) {
        toast('Nenhuma devolução disponível com esses filtros.', { type: 'warning' });
      }
      return;
    }

    if (response.StatusCode === 204) {
      setRegistros([]);
      toast('Nenhuma devolução disponível com esses filtros.', { type: 'warning' });
      return;
    }

    setRegistros([]);
    toast(response.ErroDetalhado ?? 'Não foi possível listar as devoluções.', { type: 'error' });
  };

  const repetirBusca = async () => {
    if (!ultimaBusca) return;
    const response = await DevolucaoFornecedorController.Listar(ultimaBusca);
    if (response.StatusCode === 200 && Array.isArray(response.Data)) {
      setRegistros(response.Data);
    } else {
      setRegistros([]);
    }
    setSelecionados([]);
  };

  const cancelar = async (registro: Registro) => {
    const response = await DevolucaoFornecedorController.Cancelar(registro.dvf_id);

    if (response.StatusCode === 200) {
      toast(
        `Devolução ${registro.dvf_id} cancelada. Saldo devolvível do item: ${
          response.Data?.saldo_restante ?? '—'
        }.`,
        { type: 'success' }
      );
      await repetirBusca();
      return;
    }

    toast(response.ErroDetalhado ?? 'Não foi possível cancelar esta devolução.', {
      type: 'error',
    });
  };

  // A grade é remontada sempre que os registros ou a seleção mudam: a caixa de um
  // fornecedor diferente precisa desabilitar assim que a primeira é marcada.
  useEffect(() => {
    setLinhas(
      registros.map((r) => {
        const marcado = selecionados.includes(r.dvf_id);
        const deOutroFornecedor =
          fornecedorDaSelecao !== null && r.cli_id !== fornecedorDaSelecao;

        return {
          Sel: (
            <Tooltip
              title={
                deOutroFornecedor
                  ? 'Uma nota fiscal tem um destinatário só. Limpe a seleção para escolher outro fornecedor.'
                  : ''
              }
            >
              <span>
                <Checkbox
                  size="small"
                  checked={marcado}
                  disabled={deOutroFornecedor}
                  onChange={() => alternar(r.dvf_id)}
                />
              </span>
            </Tooltip>
          ),
          Codigo: r.dvf_id,
          Data: r.data_criacao ? new Date(r.data_criacao).toLocaleDateString('pt-BR') : '',
          Fornecedor: r.fornecedor ?? '(não identificado)',
          Produto: `${r.codigo_promatec ?? ''} - ${r.produto ?? ''}`,
          NotaOrigem: r.nota_origem ?? '',
          Qtd: r.quantidade,
          Valor: formatCurrency(r.valor_total),
          Status: <Chip size="small" label={r.status} color={r.status === 'PENDENTE' ? 'default' : 'primary'} />,
          Acao: (
            <Button
              className=""
              variant="error"
              title="Cancelar"
              iconStart={<Icon.DeleteOutline />}
              onClick={() => cancelar(r)}
            />
          ),
        };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registros, selecionados]);

  const totalSelecionado = registros
    .filter((r) => selecionados.includes(r.dvf_id))
    .reduce((soma, r) => soma + Number(r.valor_total ?? 0), 0);

  const nomeDoFornecedor =
    registros.find((r) => r.cli_id === fornecedorDaSelecao)?.fornecedor ?? '';

  return (
    <Box>
      <FormBuilder fields={fields} initialValues={initialValues} onSubmit={consultar} />

      {selecionados.length > 0 && (
        <Alert
          severity="info"
          sx={{ mt: 1, mb: 1 }}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                className=""
                variant="secondary"
                title="Limpar seleção"
                onClick={() => setSelecionados([])}
              />
              <Button
                className=""
                variant="success"
                title="Gerar nota fiscal"
                iconStart={<Icon.Receipt />}
                onClick={() => setComporAberto(true)}
              />
            </Box>
          }
        >
          <strong>{selecionados.length}</strong> devoluç{selecionados.length === 1 ? 'ão' : 'ões'}{' '}
          de <strong>{nomeDoFornecedor}</strong>, somando{' '}
          <strong>{formatCurrency(totalSelecionado)}</strong>.
        </Alert>
      )}

      <ComponentTable columns={tableColumns} rows={linhas} />

      {consultou && linhas.length === 0 && (
        <Box sx={{ p: 2, color: 'text.secondary', fontSize: 14 }}>
          Nenhuma devolução disponível. Registros que já estão numa nota fiscal não
          aparecem aqui.
        </Box>
      )}

      {/* Depois de gerar, a seleção é limpa E a busca é refeita: os registros passaram a
          NOTA GERADA e somem da lista de disponíveis. Manter a seleção deixaria marcadas
          linhas que não existem mais aqui. */}
      <ModalComporNota
        aberto={comporAberto}
        dvfIds={selecionados}
        aoFechar={() => setComporAberto(false)}
        aoGerar={async () => {
          setComporAberto(false);
          setSelecionados([]);
          await repetirBusca();
        }}
      />
    </Box>
  );
};
